import {
  Button,
  IconButton,
  Card,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Menu,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Checkbox,
  FormControlLabel,
} from '@mui/material';
import { ChangeEvent, MouseEvent, useCallback, useEffect, useState } from 'react';
import { HttpAgent } from 'src/api/agent';
import HeaderBreadcrumbs from 'src/components/HeaderBreadcrumbs';
import Iconify from 'src/components/Iconify';
import Page from 'src/components/Page';
import { PATH_DASHBOARD } from 'src/routes/paths';

interface CatalogEntry {
  id?: number;
  valueCode: string;
  valueDisplayName: string;
  longDisplayName: string;
  catalogCode: string;
  isActive: boolean;
  order: number;
  ownerResourceUID: string;
  isDefault: boolean;
}

const catalogInitialState: CatalogEntry = {
  valueCode: '',
  valueDisplayName: '',
  longDisplayName: '',
  catalogCode: '',
  isActive: true,
  order: 0,
  ownerResourceUID: '',
  isDefault: false,
};

export default function CatalogList() {
  const [catalogs, setCatalogs] = useState<CatalogEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [catalogInForm, setCatalogInForm] = useState<CatalogEntry>(catalogInitialState);
  const [editingCatalog, setEditingCatalog] = useState<CatalogEntry | null>(null);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedCatalog, setSelectedCatalog] = useState<CatalogEntry | null>(null);

  const fetchCatalogs = useCallback(async () => {
    try {
      setLoading(true);
      const response = await HttpAgent.Catalog.getAll();
      setCatalogs(Array.isArray(response) ? response : []);
    } catch (error) {
      console.error('Failed to fetch catalogs', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCatalogs();
  }, [fetchCatalogs]);

  const handleOpen = () => {
    setEditingCatalog(null);
    setCatalogInForm(catalogInitialState);
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setCatalogInForm(catalogInitialState);
    setEditingCatalog(null);
  };

  const handleMenuOpen = (event: MouseEvent<HTMLButtonElement>, catalog: CatalogEntry) => {
    setAnchorEl(event.currentTarget);
    setSelectedCatalog(catalog);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedCatalog(null);
  };

  const handleEditClick = () => {
    if (selectedCatalog) {
      setEditingCatalog(selectedCatalog);
      setCatalogInForm(selectedCatalog);
      setOpen(true);
    }
    handleMenuClose();
  };
  
  const handleDeleteClick = async () => {
    if (selectedCatalog && selectedCatalog.id) {
      try {
        await HttpAgent.Catalog.delete(selectedCatalog.id);
        const deletedId = selectedCatalog.id;
        setCatalogs((prev) => prev.filter((item) => item.id !== deletedId));
      } catch (error) {
        console.error('Failed to delete catalog entry', error);
      }
    }
    handleMenuClose();
  };

  const handleInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = event.target;
    setCatalogInForm((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleSaveCatalog = async () => {
    try {
      if (editingCatalog && editingCatalog.id) {
        await HttpAgent.Catalog.update(editingCatalog.id, catalogInForm);
      } else {
        await HttpAgent.Catalog.create(catalogInForm);
      }
      handleClose();
      fetchCatalogs();
    } catch (error) {
      console.error('Failed to save catalog entry', error);
    }
  };

  return (
    <Page title="Catalogs">
      <Container>
        <HeaderBreadcrumbs
          heading=""
          title="Catalog List"
          links={[
            { name: 'Dashboard', href: PATH_DASHBOARD.root },
            { name: 'Catalogs' },
          ]}
          action={
            <Button
              variant="contained"
              startIcon={<Iconify icon={'eva:plus-fill'} />}
              onClick={handleOpen}
            >
              New Catalog Entry
            </Button>
          }
        />
        <Card>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Catalog Code</TableCell>
                  <TableCell>Value Code</TableCell>
                  <TableCell>Display Name</TableCell>
                  <TableCell>Order</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="right" />
                </TableRow>
              </TableHead>
              <TableBody>
                {catalogs.map((row, index) => (
                  <TableRow key={row.id || index}>
                    <TableCell>{row.catalogCode}</TableCell>
                    <TableCell>{row.valueCode}</TableCell>
                    <TableCell>{row.valueDisplayName}</TableCell>
                    <TableCell>{row.order}</TableCell>
                    <TableCell>{row.isActive ? 'Active' : 'Inactive'}</TableCell>
                    <TableCell align="right">
                      <IconButton onClick={(e) => handleMenuOpen(e, row)}>
                        <Iconify icon={'eva:more-vertical-fill'} width={20} height={20} />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Card>

        <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose}>
          <MenuItem onClick={handleEditClick}>
            <Iconify icon={'eva:edit-fill'} sx={{ mr: 2, width: 20, height: 20 }} />
            Edit
          </MenuItem>
          <MenuItem onClick={handleDeleteClick} sx={{ color: 'error.main' }}>
            <Iconify icon={'eva:trash-2-fill'} sx={{ mr: 2, width: 20, height: 20 }} />
            Delete
          </MenuItem>
        </Menu>

        <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
          <DialogTitle>{editingCatalog ? 'Edit Catalog Entry' : 'New Catalog Entry'}</DialogTitle>
          <DialogContent>
            <Stack spacing={3} sx={{ mt: 2 }}>
              <TextField
                name="catalogCode"
                label="Catalog Code"
                value={catalogInForm.catalogCode}
                onChange={handleInputChange}
                fullWidth
              />
              <TextField
                name="valueCode"
                label="Value Code"
                value={catalogInForm.valueCode}
                onChange={handleInputChange}
                fullWidth
              />
              <TextField
                name="valueDisplayName"
                label="Display Name"
                value={catalogInForm.valueDisplayName}
                onChange={handleInputChange}
                fullWidth
              />
              <TextField
                name="longDisplayName"
                label="Long Display Name"
                value={catalogInForm.longDisplayName}
                onChange={handleInputChange}
                fullWidth
              />
              <TextField
                name="order"
                label="Order"
                type="number"
                value={catalogInForm.order}
                onChange={handleInputChange}
                fullWidth
              />
              <FormControlLabel
                control={<Checkbox name="isActive" checked={catalogInForm.isActive} onChange={handleInputChange} />}
                label="Active"
              />
              <FormControlLabel
                control={<Checkbox name="isDefault" checked={catalogInForm.isDefault} onChange={handleInputChange} />}
                label="Default"
              />
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleClose}>Cancel</Button>
            <Button onClick={handleSaveCatalog} variant="contained">
              {editingCatalog ? 'Save' : 'Create'}
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </Page>
  );
}